import { useState, useEffect, useMemo } from "react";
import { Text, DefaultButton, Link, Pivot, PivotItem } from "@fluentui/react";
import styles from "./SupportingContent.module.css";
import { getCitationFilePath, getPdfImageFilePath, skipUnwantedTag, DataPoints } from "../../api";
import { Label } from "@fluentui/react-components";
import i18next from "../../i18n/i18n";
import { userInfo } from "../../authConfig";
import { AnalysisPanelTabs } from "../AnalysisPanel/AnalysisPanelTabs";
import {
    Globe16Regular,
    LockClosed16Regular,
    Star16Regular,
    Glance16Regular,
    DocumentText16Regular,
    Open16Regular,
    BookOpen20Regular,
    WindowNew16Regular
} from "@fluentui/react-icons";
import { getUnusedPdfImages, PdfImageSource, resolvePdfImages, takePdfImagesForRequestedPaths } from "../../utils/pdfImageUtils";
import { normalizeManualCitationDisplayPath } from "../../utils/citationUtils";

type PdfSupportingSegment = {
    htmlContent: string;
    imagePaths: string[];
};

const parsePdfSupportingContent = (content: string) => {
    const segments: PdfSupportingSegment[] = [];
    const imageContentRegex = /<imagecontent>([\s\S]*?)<\/imagecontent>/gi;
    let lastIndex = 0;
    let hasImageContentTag = false;
    let match: RegExpExecArray | null;

    while ((match = imageContentRegex.exec(content)) !== null) {
        hasImageContentTag = true;
        const htmlContent = content.slice(lastIndex, match.index);
        const imagePaths = Array.from(match[1]?.matchAll(/<imageurl>([\s\S]*?)<\/imageurl>/gi) ?? [])
            .map(([, imagePath]) => imagePath?.trim())
            .filter((imagePath): imagePath is string => Boolean(imagePath));

        if (htmlContent || imagePaths.length > 0) {
            segments.push({ htmlContent, imagePaths });
        }

        lastIndex = match.index + match[0].length;
    }

    const trailingHtmlContent = content.slice(lastIndex);
    if (trailingHtmlContent || segments.length === 0) {
        segments.push({ htmlContent: trailingHtmlContent, imagePaths: [] });
    }

    return {
        hasImageContentTag,
        segments
    };
};

const FILE_NAME_EXTENSION_PATTERN = /\.[a-z0-9]{1,10}$/i;

const isAttachmentDataPoint = (dataPoint: DataPoints): boolean => {
    const uploadDir = (dataPoint?.uploaddir_field ?? "").toLowerCase();
    const sourcePage = (dataPoint?.sourcepage_field ?? "").toLowerCase();
    const docType = (dataPoint?.doctype ?? "").toLowerCase();
    const docUrl = (dataPoint?.doc_url ?? "").toLowerCase();
    const sourceKind = String((dataPoint as DataPoints & { source_kind?: unknown }).source_kind ?? "").toLowerCase();
    return (
        uploadDir.startsWith("attachments/") ||
        sourcePage.includes("attachments/") ||
        uploadDir === "user_files" ||
        docType === "user file" ||
        docUrl.includes("/attachments/") ||
        sourceKind === "attachment"
    );
};

/**
 * Wrap referenced document file names inside the (already sanitized) thought-process HTML with links that open
 * the file, mirroring the "citation source" link in the supporting content tab. Only text nodes are touched,
 * so existing markup/attributes are never rewritten.
 *
 * @internal Exported to support unit testing.
 */
export const linkFileNamesInThoughts = (
    html: string,
    dataPoints: DataPoints[] | undefined,
    buildHref: (dataPoint: DataPoints) => string,
    options: { className?: string; title?: string } = {}
): string => {
    if (!html || !dataPoints?.length || typeof DOMParser === "undefined") return html;

    const nameToDataPoint = new Map<string, DataPoints>();
    for (const dataPoint of dataPoints) {
        if (isAttachmentDataPoint(dataPoint)) continue;
        const sourcePage = (dataPoint?.sourcepage_field ?? "").trim();
        if (!sourcePage) continue;
        const baseName = sourcePage.split("/").pop() ?? sourcePage;
        const fileName = baseName.split("#")[0].trim();
        if (!FILE_NAME_EXTENSION_PATTERN.test(fileName)) continue;
        for (const name of [sourcePage, baseName, fileName]) {
            const key = name.toLowerCase();
            if (name && !nameToDataPoint.has(key)) nameToDataPoint.set(key, dataPoint);
        }
    }
    if (!nameToDataPoint.size) return html;

    const alternation = Array.from(nameToDataPoint.keys())
        .sort((a, b) => b.length - a.length)
        .map(name => name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
        .join("|");
    const fileNameRegex = new RegExp(`(?<![A-Za-z0-9_./\\\\-])(${alternation})(?![A-Za-z0-9_])`, "gi");

    const doc = new DOMParser().parseFromString(`<body>${html}</body>`, "text/html");
    const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
    const textNodes: globalThis.Text[] = [];
    while (walker.nextNode()) {
        const node = walker.currentNode as globalThis.Text;
        if (!node.parentElement?.closest("a")) textNodes.push(node);
    }

    let changed = false;
    for (const textNode of textNodes) {
        const text = textNode.nodeValue ?? "";
        fileNameRegex.lastIndex = 0;
        if (!fileNameRegex.test(text)) continue;
        fileNameRegex.lastIndex = 0;

        const fragment = doc.createDocumentFragment();
        let lastIndex = 0;
        for (const match of text.matchAll(fileNameRegex)) {
            const dataPoint = nameToDataPoint.get(match[1].toLowerCase());
            const start = match.index ?? 0;
            if (!dataPoint) continue;
            fragment.appendChild(doc.createTextNode(text.slice(lastIndex, start)));
            const anchor = doc.createElement("a");
            anchor.href = buildHref(dataPoint);
            anchor.target = "_blank";
            anchor.rel = "noopener noreferrer";
            anchor.textContent = match[1];
            if (options.className) anchor.className = options.className;
            if (options.title) anchor.title = options.title;
            fragment.appendChild(anchor);
            lastIndex = start + match[1].length;
        }
        fragment.appendChild(doc.createTextNode(text.slice(lastIndex)));
        textNode.replaceWith(fragment);
        changed = true;
    }

    return changed ? doc.body.innerHTML : html;
};

interface Props {
    supportingContent: DataPoints[];
    index: number;
    onActiveContentChanged: (citationIndex: number) => void;
    appType: string | null;
    sourcePage: string;
    openView: any;
    sanitizedThoughts?: any;
    thoughtDataPoints?: DataPoints[];
    isThoughtsLoading?: boolean;
    supportingContentMaxHeight?: string | number;
    supportingContentTextMaxHeight?: string | number;
    fillAvailableHeight?: boolean;
}

export const SupportingContent = ({
    supportingContent,
    index,
    onActiveContentChanged,
    appType,
    sourcePage,
    openView,
    sanitizedThoughts,
    thoughtDataPoints,
    isThoughtsLoading = false,
    supportingContentMaxHeight,
    supportingContentTextMaxHeight,
    fillAvailableHeight = false
}: Props) => {
    const getActiveCitationKey = (citationIndex: number, itemCount: number) => {
        if (itemCount <= 0) {
            return "1";
        }
        if (citationIndex <= 0) {
            return "1";
        }
        return Math.min(citationIndex, itemCount).toString();
    };

    const [activeTab, setActiveTab] = useState<string | undefined>(() => getActiveCitationKey(index, supportingContent.length));
    const [selectedItem, setSelectedItem] = useState<DataPoints | undefined>(() => {
        const initialTab = getActiveCitationKey(index, supportingContent.length);
        const initialIndex = Math.max(parseInt(initialTab, 10) - 1, 0);
        return supportingContent[initialIndex];
    });
    const [selectedPreviewImage, setSelectedPreviewImage] = useState<string | null>(null);
    const [windowSize, setWindowSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight
    });
    const commonStyle: any = {
        overflowX: "auto",
        overflowY: "auto",
        borderRadius: "10px",
        display: "flex",
        flexDirection: "column",
        outline: "2px solid rgba(115, 118, 225, 1)",
        minHeight: 0,
        ...(fillAvailableHeight
            ? {
                  flex: "1 1 0",
                  height: "100%",
                  maxHeight: "100%",
                  maxWidth: "100%"
              }
            : {
                  height: supportingContentMaxHeight ?? (sourcePage === "Feedback" ? window.innerHeight - 280 : window.innerHeight - 140),
                  maxHeight: supportingContentMaxHeight ?? (sourcePage === "Feedback" ? window.innerHeight - 280 : window.innerHeight - 140),
                  maxWidth: window.innerWidth - 250
              })
    };
    const selectedFileName = (selectedItem?.sourcepage_field ?? "").split("/").pop() ?? "";
    const isFaqFile = selectedFileName.toLowerCase().startsWith("faq-");
    const selectedContentStyle: any = {
        whiteSpace: isFaqFile ? "pre-line" : "normal",
        overflowY: "auto",
        marginTop: "10px",
        minHeight: 0,
        ...(fillAvailableHeight
            ? {
                  display: "block",
                  flex: "1 1 0",
                  maxHeight: "none",
                  maxWidth: "100%"
              }
            : {
                  height: supportingContentTextMaxHeight ?? window.innerHeight - 270,
                  maxHeight: supportingContentTextMaxHeight ?? window.innerHeight - 270,
                  maxWidth: window.innerWidth - 250
              })
    };
    const navListClassName = `${styles.supportingContentNavList} ${fillAvailableHeight ? styles.supportingContentFillHeight : ""}`;
    const itemClassName = `${styles.supportingContentItem} ${fillAvailableHeight ? styles.supportingContentItemFillHeight : ""}`;
    useEffect(() => {
        const nextActiveTab = getActiveCitationKey(index, supportingContent.length);
        const contentIndex = Math.max(parseInt(nextActiveTab ?? "1", 10) - 1, 0);
        setActiveTab(nextActiveTab);
        setSelectedItem(supportingContent[contentIndex]);
        setSelectedPreviewImage(null);
    }, [supportingContent, index]);

    const makeCitationFilePath = (contentItem: DataPoints) => {
        const upload_dir = contentItem.uploaddir_field + "/" + contentItem.sourcepage_field;
        const directory =
            appType === "Manuals"
                ? "product_manual/" + contentItem.sourcepage_field
                : appType === "FAQ"
                  ? upload_dir.replace(/^[^/]*\//, "faq/")
                  : contentItem.uploaddir_field.includes("product-manuals")
                    ? "product_manual/" + contentItem.sourcepage_field
                    : upload_dir.replace(/^[^/]*\//, "faq/");
        return getCitationFilePath(directory);
    };

    const selectedContent = selectedItem?.content_field || "";
    const parsedPdfContent = parsePdfSupportingContent(selectedContent);
    const availablePdfImages = resolvePdfImages(selectedItem);
    const usedPdfImageOrders = new Set<number>();
    const segmentedPdfImages = parsedPdfContent.segments.map(segment => ({
        ...segment,
        images: takePdfImagesForRequestedPaths(segment.imagePaths, availablePdfImages, usedPdfImageOrders)
    }));
    const trailingPdfImages = getUnusedPdfImages(availablePdfImages, usedPdfImageOrders);
    const hasPdfImageContent = parsedPdfContent.hasImageContentTag || availablePdfImages.length > 0;
    const inlineResolvedContent: string | null = (() => {
        if (!parsedPdfContent.hasImageContentTag) return null;
        const seenPaths = new Set<string>();
        return selectedContent.replace(/<imagecontent>[\s\S]*?<imageurl>([\s\S]*?)<\/imageurl>[\s\S]*?<\/imagecontent>/gi, (_, rawPath) => {
            const normalizedPath = rawPath.trim().replace(/^\/+/, "").toLowerCase();
            if (!normalizedPath) return "";
            if (seenPaths.has(normalizedPath)) return "";
            seenPaths.add(normalizedPath);
            const matchedImage = availablePdfImages.find(img => (img.blobPath ?? "").replace(/^\/+/, "").toLowerCase() === normalizedPath);
            const src = matchedImage?.src ?? getPdfImageFilePath(rawPath.trim());
            return `<img src="${src}" alt="" />`;
        });
    })();
    const sanitizedSupportingContentHtml = skipUnwantedTag(
        parsedPdfContent.hasImageContentTag ? parsedPdfContent.segments.map(segment => segment.htmlContent).join("") : selectedContent
    );

    let renderedSupportingImageCount = 0;
    const renderPdfImages = (images: PdfImageSource[]) =>
        images.map(image => {
            renderedSupportingImageCount += 1;
            const imageNumber = renderedSupportingImageCount;
            const sourcePage = selectedItem?.sourcepage_field ?? "";
            const hashIndex = sourcePage.indexOf("#");
            const pathPart = hashIndex >= 0 ? sourcePage.slice(0, hashIndex) : sourcePage;
            const fragment = hashIndex >= 0 ? sourcePage.slice(hashIndex) : "";
            const fileName = pathPart.split("/").pop() ?? pathPart;
            const pageLabel = fileName.replace(/\.[^.]+$/, "");
            const imageAltText = `${fileName}${fragment} : Image ${imageNumber}`.trim();
            const ariaLabel = `${pageLabel} image ${imageNumber}`.trim();

            return (
                <button
                    key={`${selectedItem?.sourcepage_field}-supporting-image-${image.key}`}
                    type="button"
                    className={styles.supportingContentImageButton}
                    onClick={() => setSelectedPreviewImage(image.src)}
                    aria-label={ariaLabel}
                >
                    <img className={styles.supportingContentImage} src={image.src} alt={imageAltText} />
                </button>
            );
        });

    useEffect(() => {
        const handleResize = () => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight
            });
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const isThoughtProcessView = openView === AnalysisPanelTabs.ThoughtProcessTab;
    // Re-parsing the thought-process HTML is relatively costly, so only redo it when its inputs change.
    const linkedThoughts = useMemo(
        () =>
            isThoughtProcessView
                ? linkFileNamesInThoughts(sanitizedThoughts ?? "", thoughtDataPoints ?? supportingContent, makeCitationFilePath, {
                      className: styles.thoughtFileLink,
                      title: i18next.t("supportingContent.openinNewTab")
                  })
                : "",
        // makeCitationFilePath only depends on appType.
        [isThoughtProcessView, sanitizedThoughts, thoughtDataPoints, supportingContent, appType]
    );

    if (isThoughtProcessView) {
        return (
            <div className={navListClassName}>
                <div className={itemClassName} style={commonStyle}>
                    {isThoughtsLoading ? (
                        <div className={styles.thoughtSkeletonWrapper}>
                            <div className={styles.thoughtSkeletonLine}></div>
                            <div className={styles.thoughtSkeletonLine}></div>
                            <div className={styles.thoughtSkeletonLineShort}></div>
                            <div className={styles.thoughtSkeletonLine}></div>
                            <div className={styles.thoughtSkeletonLineMedium}></div>
                        </div>
                    ) : (
                        <div className={styles.thoughtProcess} dangerouslySetInnerHTML={{ __html: linkedThoughts }}></div>
                    )}
                </div>
            </div>
        );
    } else if (openView === AnalysisPanelTabs.SupportingContentTab) {
        return (
            <>
                {/* <Text>{i18next.t("supportingContent.citationInfo")}</Text> */}
                <div className={navListClassName}>
                    <div className={itemClassName} style={commonStyle}>
                        <Pivot
                            selectedKey={activeTab}
                            overflowBehavior="menu"
                            onLinkClick={(pivotItem: any) => {
                                const nextActiveTab = pivotItem?.props.itemKey ?? "1";
                                const contentIndex = Math.max(parseInt(nextActiveTab, 10) - 1, 0);
                                setActiveTab(nextActiveTab);
                                onActiveContentChanged(parseInt(nextActiveTab, 10));
                                setSelectedItem(supportingContent[contentIndex]);
                            }}
                        >
                            {supportingContent?.map((x, i) => {
                                return (
                                    <PivotItem
                                        key={i}
                                        headerText={(i + 1).toString()}
                                        itemKey={(i + 1).toString()}
                                        itemIcon={x.doctype === "Internal" ? "LockSolid" : undefined}
                                    >
                                        <div className={styles.metaCard}>
                                            <div className={styles.metaHeader}>
                                                <div className={styles.citationBadge}>
                                                    <BookOpen20Regular className={styles.citationBadgeIcon} />
                                                </div>
                                                <div className={styles.contentSourceInfo}>
                                                    <span className={styles.metaFieldLabel}>{i18next.t("supportingContent.citationSource")}</span>
                                                    <Link
                                                        className={styles.metaLink}
                                                        onClick={() => {
                                                            const path = makeCitationFilePath(x);
                                                            window.open(path, "_blank");
                                                        }}
                                                        title={i18next.t("supportingContent.openinNewTab")}
                                                    >
                                                        <span className={styles.metaLinkText}>
                                                            {(() => {
                                                                const upload_dir = x.uploaddir_field + "/" + x.sourcepage_field;
                                                                return appType === "Manuals"
                                                                    ? normalizeManualCitationDisplayPath(x.sourcepage_field)
                                                                    : appType === "FAQ"
                                                                      ? upload_dir.replace(/^[^/]*\//, "faq/")
                                                                      : x.uploaddir_field.includes("product-manuals")
                                                                        ? normalizeManualCitationDisplayPath(x.sourcepage_field)
                                                                        : upload_dir.replace(/^[^/]*\//, "faq/");
                                                            })()}
                                                        </span>
                                                        <Open16Regular className={styles.externalLinkIcon} />
                                                    </Link>
                                                </div>
                                            </div>

                                            <div className={styles.metaSourceRow}>
                                                <div className={styles.metaSourceItem}>
                                                    <Globe16Regular className={styles.metaIcon} />
                                                    <div className={styles.metaSourceContent}>
                                                        <span className={styles.metaFieldLabel}>{i18next.t("supportingContent.originalSource")}</span>
                                                        <Link
                                                            className={styles.metaLink}
                                                            onClick={() => window.open(x.doc_url, "_blank")}
                                                            title={i18next.t("supportingContent.openinNewTab")}
                                                        >
                                                            <span className={styles.metaLinkText}>{x.doc_url}</span>
                                                        </Link>
                                                    </div>
                                                </div>
                                                {x.hasPassword && (
                                                    <>
                                                        <div className={styles.metaVerticalDivider} />
                                                        <div className={styles.metaSourceItem}>
                                                            <LockClosed16Regular className={styles.metaIcon} />
                                                            <div className={styles.metaSourceContent}>
                                                                <span className={styles.metaFieldLabel}>{i18next.t("supportingContent.password")}</span>
                                                                <Link
                                                                    className={styles.metaLink}
                                                                    onClick={() =>
                                                                        window.open("https://support.worksap.co.jp/downloadShowPassword.do", "_blank")
                                                                    }
                                                                    title={i18next.t("supportingContent.openinNewTab")}
                                                                >
                                                                    <span className={styles.metaLinkText}>
                                                                        {"https://support.worksap.co.jp/downloadShowPassword.do"}
                                                                    </span>
                                                                </Link>
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                            </div>

                                            {(userInfo.isWap === "true" || userInfo.isWap == "True") && (
                                                <div className={styles.metaFooterRow}>
                                                    <div className={styles.metaScoreGroup}>
                                                        <div className={styles.metaScoreItem}>
                                                            <div className={styles.metaScoreItemHeader}>
                                                                <Star16Regular className={styles.metaIcon} />
                                                                <span className={styles.metaFieldLabel}>{i18next.t("supportingContent.score")}</span>
                                                            </div>
                                                            <span className={styles.metaScoreValue}>{x.search_score}</span>
                                                        </div>
                                                        <div className={styles.metaScoreItem}>
                                                            <div className={styles.metaScoreItemHeader}>
                                                                <Glance16Regular className={styles.metaIcon} />
                                                                <span className={styles.metaFieldLabel}>{i18next.t("supportingContent.rerankerScore")}</span>
                                                            </div>
                                                            <span className={styles.metaScoreValue}>{x.search_reranker_score}</span>
                                                        </div>
                                                        <div className={styles.metaScoreItem}>
                                                            <div className={styles.metaScoreItemHeader}>
                                                                <DocumentText16Regular className={styles.metaIcon} />
                                                                <span className={styles.metaFieldLabel}>{i18next.t("supportingContent.documenttype")}</span>
                                                            </div>
                                                            <span
                                                                className={styles.metaScoreValue}
                                                                style={{ color: x.doctype === "Internal" ? "red" : "inherit" }}
                                                            >
                                                                {i18next.t(`chat.${x.doctype}`)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <DefaultButton
                                                        className={styles.openNewWindowButton}
                                                        onClick={() => {
                                                            const path = makeCitationFilePath(x);
                                                            window.open(path, "_blank", `width=${window.innerWidth},height=${window.innerHeight}`);
                                                        }}
                                                        title={i18next.t("supportingContent.openinNewWindow")}
                                                    >
                                                        {i18next.t("supportingContent.openinNewWindow")}
                                                        <WindowNew16Regular className={styles.openNewWindowIcon} />
                                                    </DefaultButton>
                                                </div>
                                            )}
                                        </div>
                                    </PivotItem>
                                );
                            })}
                            {supportingContent?.length === 0 && (
                                <PivotItem key={0} headerText={i18next.t("supportingContent.noContent")} itemKey={(1).toString()} itemIcon="PageRemove">
                                    <Label>{i18next.t("supportingContent.noSupportingContent")}</Label>
                                </PivotItem>
                            )}
                        </Pivot>
                        {activeTab && (
                            <div className={styles.contentCard} style={selectedContentStyle}>
                                {hasPdfImageContent ? (
                                    parsedPdfContent.hasImageContentTag ? (
                                        <div
                                            className={`${styles.supportingContentItemText} ${styles.supportingContentHtml}`}
                                            dangerouslySetInnerHTML={{ __html: skipUnwantedTag(inlineResolvedContent!) }}
                                            onClick={e => {
                                                const img = (e.target as HTMLElement).closest?.("img");
                                                if (img) setSelectedPreviewImage((img as HTMLImageElement).src);
                                            }}
                                        ></div>
                                    ) : (
                                        <div className={styles.supportingContentRichBody}>
                                            {segmentedPdfImages.map((segment, segmentIndex) => (
                                                <div key={`${selectedItem?.sourcepage_field}-segment-${segmentIndex}`}>
                                                    {segment.htmlContent && (
                                                        <div
                                                            className={styles.supportingContentHtml}
                                                            dangerouslySetInnerHTML={{ __html: skipUnwantedTag(segment.htmlContent) }}
                                                        ></div>
                                                    )}
                                                    {renderPdfImages(segment.images)}
                                                </div>
                                            ))}
                                            {renderPdfImages(trailingPdfImages)}
                                            {availablePdfImages.length === 0 && (selectedItem?.pdf_image_urls?.length ?? 0) > 0 && (
                                                <Text className={styles.supportingContentItemText}>{i18next.t("supportingContent.imageUnavailable")}</Text>
                                            )}
                                        </div>
                                    )
                                ) : (
                                    <div
                                        className={`${styles.supportingContentItemText} ${styles.supportingContentHtml}`}
                                        dangerouslySetInnerHTML={{ __html: sanitizedSupportingContentHtml }}
                                    ></div>
                                )}
                            </div>
                        )}
                        {activeTab && <p className={styles.viewFullContentNote}>{i18next.t("supportingContent.viewFullContent")}</p>}
                    </div>
                </div>
                {selectedPreviewImage && (
                    <div className={styles.supportingContentImageOverlay} role="dialog" aria-modal="true" onClick={() => setSelectedPreviewImage(null)}>
                        <div className={styles.supportingContentImageDialog} onClick={event => event.stopPropagation()}>
                            <button
                                type="button"
                                className={styles.supportingContentImageCloseButton}
                                onClick={() => setSelectedPreviewImage(null)}
                                aria-label={i18next.t("answer.closeImagePreviewAriaLabel", { defaultValue: "Close image preview" })}
                            >
                                x
                            </button>
                            <img
                                className={styles.supportingContentImageFull}
                                src={selectedPreviewImage}
                                alt={(() => {
                                    const sp = selectedItem?.sourcepage_field ?? "";
                                    const hi = sp.indexOf("#");
                                    const file = (hi >= 0 ? sp.slice(0, hi) : sp).split("/").pop() ?? sp;
                                    const frag = hi >= 0 ? sp.slice(hi) : "";
                                    return `${file}${frag} full image`.trim();
                                })()}
                            />
                        </div>
                    </div>
                )}
            </>
        );
    }
};
